import { useTranslation } from 'react-i18next';
import { DASH, fmtPercent, heatmapCellStyle } from '../../Utils/awarenessDepartments';

/**
 * Heatmap reparti x moduli: ogni cella è la percentuale di completamento di quel
 * modulo del percorso in quel reparto. Serve a individuare i comparti scoperti
 * su un tema specifico.
 *
 * Riceve la heatmap già normalizzata da `normalizeHeatmap`.
 */
export default function DepartmentHeatmap({ heatmap, smallSampleUsers, onSelectDepartment }) {
  const { t } = useTranslation();

  const modules = heatmap?.modules || [];
  const departments = heatmap?.departments || [];

  if (!modules.length || !departments.length) {
    return (
      <div className="p-6 text-center text-gray-500 text-sm">
        {t('awarenessDepartments.heatmapEmpty')}
      </div>
    );
  }

  const legend = [
    { label: '0%', className: 'bg-red-100' },
    { label: '1-19%', className: 'bg-red-300' },
    { label: '20-39%', className: 'bg-orange-300' },
    { label: '40-59%', className: 'bg-amber-300' },
    { label: '60-79%', className: 'bg-emerald-300' },
    { label: '80-100%', className: 'bg-emerald-500' },
  ];

  return (
    <div>
      <div className="px-4 py-2 flex items-center gap-2 flex-wrap border-b border-gray-100">
        <span className="text-[11px] text-gray-500">{t('awarenessDepartments.heatmapLegend')}:</span>
        {legend.map((item) => (
          <span key={item.label} className="flex items-center gap-1 text-[11px] text-gray-600">
            <span className={`w-3 h-3 rounded-sm ${item.className}`} />
            {item.label}
          </span>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-white text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-4 py-2 align-bottom border-b border-gray-200 w-[200px] min-w-[200px]">
                {t('awarenessDepartments.department')}
              </th>
              {modules.map((module) => (
                <th
                  key={module.id}
                  className="px-0.5 py-2 align-bottom border-b border-gray-200"
                  title={module.title}
                >
                  <div className="flex justify-center">
                    {/* Testo verticale che va a capo: l'altezza limita la lunghezza
                        della riga, la larghezza quante righe può occupare. */}
                    <div
                      className="text-[11px] font-medium text-gray-600 leading-tight h-32 w-10 break-words"
                      style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                    >
                      {module.title}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {departments.map((dept) => {
              const byModule = new Map((dept.modules || []).map((m) => [m.id, m]));
              return (
                <tr key={dept.department_id} className="group">
                  <td className="sticky left-0 z-10 bg-white group-hover:bg-yellow-50 px-4 py-1.5 border-b border-gray-100 w-[200px] min-w-[200px]">
                    <button
                      type="button"
                      onClick={() => onSelectDepartment && onSelectDepartment(dept.department_id)}
                      className="text-left w-full"
                    >
                      <span className="text-sm font-medium text-gray-800 group-hover:text-blue-700">
                        {dept.department_name || DASH}
                      </span>
                      <span className="block text-[11px] text-gray-400">
                        {t('awarenessDepartments.activeUsersCount', { count: dept.users_count ?? 0 })}
                        {dept.small_sample && (
                          <span title={t('awarenessDepartments.smallSampleHint', { count: smallSampleUsers })}> *</span>
                        )}
                      </span>
                    </button>
                  </td>
                  {modules.map((module) => {
                    const cell = byModule.get(module.id);
                    const rate = cell ? cell.completionRate : null;
                    return (
                      <td key={module.id} className="p-0.5 border-b border-gray-100">
                        <div
                          className={`w-full min-w-[34px] h-8 flex items-center justify-center rounded text-[10px] font-semibold ${heatmapCellStyle(rate)}`}
                          title={`${dept.department_name || DASH} · ${module.title}: ${fmtPercent(rate, 0)}`}
                        >
                          {rate === null || rate === undefined ? DASH : Math.round(Number(rate))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
